import { Collapse } from "@mui/material";
import React from "react";
import { ProductCard } from "@/features/product/ProductCard";
import { useRecommendedProducts } from "@/features/recommended-products";
import { TransitionGroup } from "react-transition-group";
import { useCartUiActions } from "./cartStore";
import {
  CartAddBodyWithTracking,
  shopifyCartApi,
} from "@/services/shopifyCart";
import { SizedImage } from "@/components/SizedImage";
import { Button } from "@/components/Button";
import { DroppsProduct, productSelect } from "@/store";

export interface RecommendedProductListProps {
  showShopNow: boolean;
  cardsShown?: number;
  headline: string;
  subHeadline?: string;
}

export const RecommendedProductCardList = ({
  cardsShown = 4,
  showShopNow,
  headline,
  subHeadline,
}: RecommendedProductListProps) => {
  const recommendedProducts = useRecommendedProducts();
  const topProducts = recommendedProducts.slice(0, cardsShown);

  if (topProducts.length === 0) {
    return null;
  }

  return (
    <div className="recommended-products flex flex-col px-1">
      {headline ? (
        <div className="px-4 py-6 text-center">
          <h3 className="title text-xl">{headline}</h3>
          {subHeadline ? <p className="body pt-2">{subHeadline}</p> : null}
        </div>
      ) : null}
      <TransitionGroup component={"ul"}>
        {topProducts.map((x) => (
          <Collapse key={x.id} component={"li"}>
            <hr className="divider-x mx-4 my-0" />
            <RecommendedProductCard showShopNow={showShopNow} product={x} />
          </Collapse>
        ))}
      </TransitionGroup>
    </div>
  );
};

const RecommendedProductCard = ({
  showShopNow,
  product,
}: {
  showShopNow: boolean;
  product: DroppsProduct;
}) => {
  const shopifyCartAdder = shopifyCartApi.useTrackedAdd();
  const cartActions = useCartUiActions();
  const variant = productSelect.defaultVariant(product);
  const productUrl = productSelect.url(product);

  const productCardProduct = {
    featuredImage: productSelect.featuredImage(product),
    featuredText: product.featuredQuote,
    title: product.title,
    price: undefined,
  };

  const closeCart = () => cartActions.closeDrawer();
  return (
    <ProductCard
      className="!bg-transparent [&_.product-card-scent-name]:bg-white"
      productCardValue={productCardProduct}
      cover={
        <a href={productUrl} onClick={closeCart}>
          <div className="w-20 shrink-0 [&>img]:max-w-full">
            <SizedImage
              src={productCardProduct.featuredImage.url}
              alt={productCardProduct.title}
              height={80}
              width={80}
            />
          </div>
        </a>
      }
      productTitle={
        <a href={productUrl} onClick={closeCart}>
          <h4
            data-id="product-card-title"
            className="title text-sm transition hover:underline"
          >
            {productSelect.name(productCardProduct)}
          </h4>
        </a>
      }
      compact={true}
      hoverable={false}
      rounded={false}
      actions={
        <div className="ml-24 flex items-center">
          <Button
            className="text-sm normal-case tracking-normal"
            variant="text"
            onClick={async () => {
              const payload: CartAddBodyWithTracking = {
                items: [{ id: variant.id, quantity: 1 }],
                sourceModule: "cart_recommendation",
              };
              shopifyCartAdder.mutate(payload);
            }}
            loading={shopifyCartAdder.isLoading}
          >
            Add to Cart{" "}
            <span className="sr-only">{productCardProduct.title}</span>
          </Button>
          {showShopNow && (
            <>
              <span className="mx-1.5">|</span>
              <Button
                className="text-sm normal-case tracking-normal"
                href={productUrl}
                variant="text"
                onClick={closeCart}
                aria-label={`Shop ${product.title}`}
              >
                Shop Now
              </Button>
            </>
          )}
        </div>
      }
    />
  );
};
